import type { CSSProperties } from 'react';
import type {
  CriticalBoundingBoxByScreen,
  VideoFrameStyle,
  ViewportSizeReference,
  ViewportBoundingBox,
} from '@/app/Teleop/teleopConfig';

type SelectViewportProfileParams = {
  viewportWidthPx: number;
  viewportHeightPx: number;
  mobileMaxWidthPx: number;
  tabletMaxWidthPx: number;
};

type SelectCriticalBoundingBoxParams = SelectViewportProfileParams & {
  criticalBoundingBoxByScreen: CriticalBoundingBoxByScreen;
};

type ComputeCriticalVideoStyleParams = {
  viewportWidthPx: number;
  viewportHeightPx: number;
  sourceWidthPx: number;
  sourceHeightPx: number;
  criticalBox: ViewportBoundingBox;
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

export const isFirefoxBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent;
  const firefoxBrowser = /Firefox\//.test(ua);

  return firefoxBrowser;
};

export const isMobileSafariBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const isIOSDevice = /iP(hone|ad|od)/.test(ua);
  const isIPadDesktopUA = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = isIOSDevice || isIPadDesktopUA;
  const isAppleWebKit = /WebKit/.test(ua);
  const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isAppleWebKit && !isOtherIOSBrowser;
};

export const isCompatibilityVideoFramingBrowser = (): boolean => {
  const mobileSafariBrowser = isMobileSafariBrowser();

  if (mobileSafariBrowser) {
    return true;
  }

  const firefoxBrowser = isFirefoxBrowser();

  return firefoxBrowser;
};

export type ViewportProfile =
  | 'mobilePortrait'
  | 'mobileLandscape'
  | 'tabletPortrait'
  | 'tabletLandscape'
  | 'desktop';

export const selectViewportProfile = (params: SelectViewportProfileParams): ViewportProfile => {
  const { viewportWidthPx, viewportHeightPx, mobileMaxWidthPx, tabletMaxWidthPx } = params;
  const isPortrait = viewportHeightPx >= viewportWidthPx;
  if (viewportWidthPx <= mobileMaxWidthPx) {
    return isPortrait ? 'mobilePortrait' : 'mobileLandscape';
  }
  if (viewportWidthPx <= tabletMaxWidthPx) {
    return isPortrait ? 'tabletPortrait' : 'tabletLandscape';
  }
  return 'desktop';
};

export const selectCriticalBoundingBox = (params: SelectCriticalBoundingBoxParams): ViewportBoundingBox => {
  const { criticalBoundingBoxByScreen } = params;
  const profile = selectViewportProfile(params);
  return criticalBoundingBoxByScreen[profile];
};

export const computeManualVideoStyle = (frameStyle: VideoFrameStyle): CSSProperties => {
  return {
    position: 'absolute',
    left: `${frameStyle.leftPct}%`,
    top: `${frameStyle.topPct}%`,
    width: `${frameStyle.widthPct}%`,
    height: `${frameStyle.heightPct}%`,
    objectFit: 'cover',
    objectPosition: `${frameStyle.objectPositionXPct}% ${frameStyle.objectPositionYPct}%`,
  };
};

export const adaptManualFrameStyleToViewport = (params: {
  frameStyle: VideoFrameStyle;
  referenceViewport: ViewportSizeReference;
  targetViewportWidthPx: number;
  targetViewportHeightPx: number;
}): VideoFrameStyle => {
  const { frameStyle, referenceViewport, targetViewportWidthPx, targetViewportHeightPx } = params;
  if (
    referenceViewport.widthPx <= 0 ||
    referenceViewport.heightPx <= 0 ||
    targetViewportWidthPx <= 0 ||
    targetViewportHeightPx <= 0
  ) {
    return frameStyle;
  }

  const centerXPct = frameStyle.leftPct + frameStyle.widthPct / 2;
  const centerYPct = frameStyle.topPct + frameStyle.heightPct / 2;
  const heightScale =
    (referenceViewport.heightPx / referenceViewport.widthPx) *
    (targetViewportWidthPx / targetViewportHeightPx);
  const adjustedHeightPct = frameStyle.heightPct * heightScale;
  const clampedHeightPct = clamp(adjustedHeightPct, 1, 100);
  const adjustedTopPct = centerYPct - clampedHeightPct / 2;
  const clampedWidthPct = clamp(frameStyle.widthPct, 1, 100);
  const adjustedLeftPct = centerXPct - clampedWidthPct / 2;

  return {
    ...frameStyle,
    widthPct: clampedWidthPct,
    heightPct: clampedHeightPct,
    leftPct: clamp(adjustedLeftPct, 0, 100 - clampedWidthPct),
    topPct: clamp(adjustedTopPct, 0, 100 - clampedHeightPct),
  };
};

export const computeCriticalVideoStyle = (params: ComputeCriticalVideoStyleParams): CSSProperties => {
  const { viewportWidthPx, viewportHeightPx, sourceWidthPx, sourceHeightPx, criticalBox } = params;
  const coverScale = Math.max(viewportWidthPx / sourceWidthPx, viewportHeightPx / sourceHeightPx);
  const visibleWidthAtCoverInSourcePx = viewportWidthPx / coverScale;
  const visibleHeightAtCoverInSourcePx = viewportHeightPx / coverScale;

  const boxLeftPx = (criticalBox.leftPct / 100) * sourceWidthPx;
  const boxTopPx = (criticalBox.topPct / 100) * sourceHeightPx;
  const boxWidthPx = (criticalBox.widthPct / 100) * sourceWidthPx;
  const boxHeightPx = (criticalBox.heightPct / 100) * sourceHeightPx;
  const boxRightPx = boxLeftPx + boxWidthPx;
  const boxBottomPx = boxTopPx + boxHeightPx;

  const defaultCenterXPx = boxLeftPx + boxWidthPx / 2;
  const defaultCenterYPx = boxTopPx + boxHeightPx / 2;

  const scaleDownToFitCritical = Math.min(
    1,
    visibleWidthAtCoverInSourcePx / boxWidthPx,
    visibleHeightAtCoverInSourcePx / boxHeightPx
  );
  const visibleWidthInSourcePx = visibleWidthAtCoverInSourcePx / scaleDownToFitCritical;
  const visibleHeightInSourcePx = visibleHeightAtCoverInSourcePx / scaleDownToFitCritical;

  const sourceMinCenterXPx = visibleWidthInSourcePx / 2;
  const sourceMaxCenterXPx = sourceWidthPx - visibleWidthInSourcePx / 2;
  const sourceMinCenterYPx = visibleHeightInSourcePx / 2;
  const sourceMaxCenterYPx = sourceHeightPx - visibleHeightInSourcePx / 2;

  let clampedCenterXPx = clamp(defaultCenterXPx, sourceMinCenterXPx, sourceMaxCenterXPx);
  let clampedCenterYPx = clamp(defaultCenterYPx, sourceMinCenterYPx, sourceMaxCenterYPx);

  if (visibleWidthInSourcePx >= boxWidthPx) {
    const minCenterToKeepBoxVisibleX = boxRightPx - visibleWidthInSourcePx / 2;
    const maxCenterToKeepBoxVisibleX = boxLeftPx + visibleWidthInSourcePx / 2;
    clampedCenterXPx = clamp(clampedCenterXPx, minCenterToKeepBoxVisibleX, maxCenterToKeepBoxVisibleX);
  }
  if (visibleHeightInSourcePx >= boxHeightPx) {
    const minCenterToKeepBoxVisibleY = boxBottomPx - visibleHeightInSourcePx / 2;
    const maxCenterToKeepBoxVisibleY = boxTopPx + visibleHeightInSourcePx / 2;
    clampedCenterYPx = clamp(clampedCenterYPx, minCenterToKeepBoxVisibleY, maxCenterToKeepBoxVisibleY);
  }

  const centerXPct = (clampedCenterXPx / sourceWidthPx) * 100;
  const centerYPct = (clampedCenterYPx / sourceHeightPx) * 100;
  const scaledLayoutPercent = 100 / scaleDownToFitCritical;
  const scaledOffsetPercent = (scaledLayoutPercent - 100) / 2;

  return {
    position: 'absolute',
    left: `-${scaledOffsetPercent}%`,
    top: `-${scaledOffsetPercent}%`,
    width: `${scaledLayoutPercent}%`,
    height: `${scaledLayoutPercent}%`,
    objectFit: 'cover',
    objectPosition: `${centerXPct}% ${centerYPct}%`,
    transform: `scale(${scaleDownToFitCritical})`,
    transformOrigin: '50% 50%',
  };
};
